import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/user-plus.svg";
import { ReactComponent as LogOutIcon } from "feather-icons/dist/icons/log-out.svg";
import useRequest from "../../hooks/useRequest";

// Styled Components
const Header = tw.header`flex justify-between items-center md:py-4 px-4 py-2 fixed top-0 w-full z-50 bg-white shadow-md backdrop-blur-3xl`;
const NavLinks = tw.div`flex items-center`;
const NavLink = tw(Link)`
  text-lg md:my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#ab3d63] text-[#ab3d63]
`;
const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-[#ab3d63] text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;
const CartLink = tw(NavLink)`
  inline-block lg:ml-6 relative
`;
const CartBadge = tw.span`
  absolute -top-[8px] -right-[8px] bg-[#ab3d63] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center
`;
const LogoutButton = tw.button`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#ab3d63] text-[#ab3d63] flex items-center lg:ml-6
`;
const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`}
  img {
    ${tw`md:w-32 w-24 mr-3`}
  }
`;

// Updated Mobile Menu Styles
const MobileNavLinksContainer = tw.nav`lg:hidden flex flex-1 items-center justify-between`;
const NavToggle = tw.button`
  lg:hidden z-50 focus:outline-none text-[#ab3d63] hocus:text-[#ab3d63] transition duration-300 p-2 rounded-full hover:bg-gray-100
`;
const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden fixed top-0 right-0 h-full w-[80%] max-w-[300px] bg-white shadow-2xl z-40 flex flex-col pt-20 px-6`}
  ${NavLinks} {
    ${tw`flex flex-col gap-4`}
  }
`);
const MobileNavOverlay = motion(styled.div`
  ${tw`lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40`}
`);
const MobileNavLink = styled(NavLink)`
  ${tw`text-xl px-4 rounded-lg hover:bg-gray-100 transition-all duration-200 text-[#ab3d63] font-semibold`}
`;
const MobilePrimaryLink = styled(PrimaryLink)`
  ${tw`text-center px-6 rounded-lg mt-4`}
`;
const MobileLogoutButton = styled(LogoutButton)`
  ${tw`text-xl px-4 rounded-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2`}
`;
const MobileCartLink = styled(CartLink)`
  ${tw`text-xl px-4 rounded-lg hover:bg-gray-100 transition-all duration-200 relative`}
`;

const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

// Modal Styles
const ModalOverlay = tw.div`fixed inset-0 bg-black bg-opacity-[0.6] z-50 flex justify-center items-center p-4`;
const ModalContent = styled.div`
  ${tw`min-w-[400px] w-full bg-gradient-to-b from-[#fef9fd] to-white shadow-2xl rounded-2xl p-4 relative max-h-[80vh] border border-[#ab3d6350] transform transition-all duration-300`}
`;
const CloseButton = tw.button`absolute top-[16px] right-[16px] text-gray-600 hover:text-[#ab3d63] focus:outline-none z-10 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200`;
const ModalFormContainer = tw.div`w-full mt-8`;
const ModalForm = tw.form`flex flex-col gap-5`;
const ModalInput = tw.input`w-full px-5 py-3 rounded-xl bg-gray-100 border border-gray-300 placeholder-gray-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#ab3d63] focus:border-transparent focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md`;
const ModalSubmitButton = styled.button`
  ${tw`font-semibold bg-[#ab3d63] text-white w-full py-3 rounded-xl hover:bg-[#c9386b] transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#ab3d63] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`}
  .icon {
    ${tw`w-5 h-5 mr-2`}
  }
  .text {
    ${tw`ml-2`}
  }
`;
const ModalErrorMessage = tw.div`text-red-500 text-sm mt-3 text-center font-medium animate-pulse`;
const ModalSuccessMessage = tw.div`text-green-500 text-sm mt-3 text-center font-medium animate-bounce`;
const ModalLinkContainer = tw.div`mt-4 flex justify-center text-sm text-gray-600 gap-4`;
const ModalLink = tw.button`font-medium hover:text-[#ab3d63] hover:underline transition-colors duration-200`;

// Login and SignUp Styles
const Heading = tw.h1`text-3xl font-extrabold text-[#ab3d63] mb-3 tracking-tight text-center`;
const LogoImage = tw.img`h-[50px] mx-auto transition-transform duration-300 hover:scale-110 mb-4`;

// Inline SVGs
const CartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    tw="w-6 h-6"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    tw="w-6 h-6"
  >
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    tw="w-6 h-6"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Modal Component
const Modal = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: -50 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalContent>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
          {children}
        </ModalContent>
      </motion.div>
    </ModalOverlay>
  );
};

// Login Form Component
const LoginForm = ({ onClose, navigate }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { makeRequest, isLoading, errorMessage, data } = useRequest();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await makeRequest({
        method: "POST",
        url: "/api/auth/login",
        data: { email, password, userType: "endUser" },
      });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("email", email);
      localStorage.setItem("userName", response?.data?.name);
      setEmail("");
      setPassword("");
      onClose();
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  return (
    <>
      <LogoImage src="https://juiscakeimages.s3.ca-central-1.amazonaws.com/live/Web-Header-Logo-01.png" />
      <Heading>Sign In</Heading>
      <ModalFormContainer>
        <ModalForm onSubmit={handleSubmit}>
          <ModalInput
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <ModalInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <ModalSubmitButton type="submit" disabled={isLoading}>
            <LoginIcon className="icon" />
            <span className="text">{isLoading ? "Signing In..." : "Sign In"}</span>
          </ModalSubmitButton>
          {errorMessage && <ModalErrorMessage>{errorMessage}</ModalErrorMessage>}
          {data?.success ? (
            <ModalSuccessMessage>Login successful!</ModalSuccessMessage>
          ) : (
            data?.data && <ModalErrorMessage>{data.data}</ModalErrorMessage>
          )}
        </ModalForm>
        <ModalLinkContainer>
          <ModalLink onClick={() => onClose("signup")}>Sign Up</ModalLink>
        </ModalLinkContainer>
      </ModalFormContainer>
    </>
  );
};

// SignUp Form Component
const SignUpForm = ({ onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const { makeRequest, isLoading, errorMessage } = useRequest();

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Password must contain at least one number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");
    if (!validateForm()) return;
    try {
      const response = await makeRequest({
        method: "POST",
        url: "/api/users",
        data: { name, email, role: "endUser", password, userType: "endUser" },
      });
      if (response?.success?.message) {
        setSuccessMessage("Sign up successful! Please sign in.");
        setName("");
        setEmail("");
        setPassword("");
        setTimeout(() => {
          onClose("login");
        }, 2000);
      } else {
        setErrors({ api: response?.data?.message || "Sign up failed. Please try again." });
      }
    } catch (error) {
      console.error("Sign up error: ", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred during sign-up";
      setErrors({ api: errorMsg });
    }
  };

  return (
    <>
      <LogoImage src="https://juiscakeimages.s3.ca-central-1.amazonaws.com/live/Web-Header-Logo-01.png" />
      <Heading>Sign Up</Heading>
      <ModalFormContainer>
        <ModalForm onSubmit={handleSubmit}>
          <div>
            <ModalInput
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              aria-invalid={errors.name ? "true" : "false"}
            />
            {errors.name && <ModalErrorMessage>{errors.name}</ModalErrorMessage>}
          </div>
          <div>
            <ModalInput
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-invalid={errors.email ? "true" : "false"}
            />
            {errors.email && <ModalErrorMessage>{errors.email}</ModalErrorMessage>}
          </div>
          <div>
            <ModalInput
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-invalid={errors.password ? "true" : "false"}
            />
            {errors.password && <ModalErrorMessage>{errors.password}</ModalErrorMessage>}
          </div>
          <ModalSubmitButton type="submit" disabled={isLoading}>
            <SignUpIcon className="icon" />
            <span className="text">{isLoading ? "Signing Up..." : "Sign Up"}</span>
          </ModalSubmitButton>
          {errors.api && <ModalErrorMessage>{errors.api}</ModalErrorMessage>}
          {successMessage && <ModalSuccessMessage>{successMessage}</ModalSuccessMessage>}
        </ModalForm>
        <ModalLinkContainer>
          <ModalLink onClick={() => onClose("login")}>Sign In</ModalLink>
        </ModalLinkContainer>
      </ModalFormContainer>
    </>
  );
};

// Header Component
const HeaderComponent = ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
  const { showNavLinks, toggleNavbar, closeNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];
  const [cartItems, setCartItems] = useState([]);
  const [modalType, setModalType] = useState(null);
  const isLoggedIn = localStorage.getItem("userName");
  const navigate = useNavigate();

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");
    setCartItems(storedCart);
  }, []);

  const handleModalOpen = (type) => {
    setModalType(type);
    closeNavbar();
  };

  const handleModalClose = (switchTo) => {
    setModalType(switchTo || null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("userName");
    navigate("/");
    closeNavbar();
    window.location.reload();
  };

  const handleNavLinkClick = () => {
    closeNavbar();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const defaultLinks = [
    <NavLinks key={1}>
      <MobileNavLink to="/" onClick={handleNavLinkClick}>Home</MobileNavLink>
      <MobileNavLink to="/category/cake" onClick={handleNavLinkClick}>Cake</MobileNavLink>
      <MobileNavLink to="/category/treats" onClick={handleNavLinkClick}>Treats</MobileNavLink>
      <MobileNavLink to="/aboutus" onClick={handleNavLinkClick}>About</MobileNavLink>
      <MobileNavLink to="/contactus" onClick={handleNavLinkClick}>Contact Us</MobileNavLink>
      <MobileNavLink to="/blogs" onClick={handleNavLinkClick}>Blogs</MobileNavLink>
      <MobileNavLink to="/my-orders" onClick={handleNavLinkClick}>My Orders</MobileNavLink>
      {!isLoggedIn ? (
        <>
          <MobileNavLink as="button" onClick={() => handleModalOpen("login")}>
            Login
          </MobileNavLink>
          <MobilePrimaryLink
            css={roundedHeaderButton && tw`rounded-full`}
            as="button"
            onClick={() => handleModalOpen("signup")}
          >
            Sign Up
          </MobilePrimaryLink>
        </>
      ) : (
        <MobileLogoutButton onClick={handleLogout}>
          <LogOutIcon tw="w-6 h-6" />
          <span tw="ml-2">Logout</span>
        </MobileLogoutButton>
      )}
      <MobileCartLink to="/cart" onClick={handleNavLinkClick}>
        <CartIcon />
        {cartItems.length > 0 && <CartBadge>{cartItems.length}</CartBadge>}
      </MobileCartLink>
    </NavLinks>,
  ];

  const defaultLogoLink = (
    <LogoLink to="/" onClick={handleNavLinkClick}>
      <img
        src="https://juiscakeimages.s3.ca-central-1.amazonaws.com/live/Web-Header-Logo-01.png"
        alt="logo"
      />
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <>
      <Header className={className || "header-light"}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLink}
          <NavLinks>
            <NavLink to="/" onClick={handleNavLinkClick}>Home</NavLink>
            <NavLink to="/category/cake" onClick={handleNavLinkClick}>Cake</NavLink>
            <NavLink to="/category/treats" onClick={handleNavLinkClick}>Treats</NavLink>
            <NavLink to="/aboutus" onClick={handleNavLinkClick}>About</NavLink>
            <NavLink to="/contactus" onClick={handleNavLinkClick}>Contact Us</NavLink>
            <NavLink to="/blogs" onClick={handleNavLinkClick}>Blogs</NavLink>
            <NavLink to="/my-orders" onClick={handleNavLinkClick}>My Orders</NavLink>
            {!isLoggedIn ? (
              <>
                <NavLink as="button" onClick={() => handleModalOpen("login")} tw="lg:ml-12!">
                  Login
                </NavLink>
                <PrimaryLink
                  css={roundedHeaderButton && tw`rounded-full`}
                  as="button"
                  onClick={() => handleModalOpen("signup")}
                >
                  Sign Up
                </PrimaryLink>
              </>
            ) : (
              <LogoutButton onClick={handleLogout}>
                <LogOutIcon tw="w-6 h-6" />
                <span tw="ml-2">Logout</span>
              </LogoutButton>
            )}
            <CartLink to="/cart" onClick={handleNavLinkClick}>
              <CartIcon />
              {cartItems.length > 0 && <CartBadge>{cartItems.length}</CartBadge>}
            </CartLink>
          </NavLinks>
        </DesktopNavLinks>
        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLink}
          <NavToggle onClick={toggleNavbar}>
            {showNavLinks ? <CloseIcon /> : <MenuIcon />}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
      <AnimatePresence>
        {showNavLinks && (
          <>
            <MobileNavOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeNavbar}
            />
            <MobileNavLinks
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {links}
            </MobileNavLinks>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {modalType === "login" && (
          <Modal isOpen={modalType === "login"} onClose={handleModalClose}>
            <LoginForm onClose={handleModalClose} navigate={navigate} />
          </Modal>
        )}
        {modalType === "signup" && (
          <Modal isOpen={modalType === "signup"} onClose={handleModalClose}>
            <SignUpForm onClose={handleModalClose} />
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`xl:hidden`,
    desktopNavLinks: tw`xl:flex`,
    mobileNavLinksContainer: tw`xl:hidden`,
  },
};

const useAnimatedNavToggler = () => {
  const [showNavLinks, setShowNavLinks] = useState(false);
  const toggleNavbar = () => setShowNavLinks(!showNavLinks);
  const closeNavbar = () => setShowNavLinks(false);
  return { showNavLinks, toggleNavbar, closeNavbar };
};

export default HeaderComponent;